/**
 * useConfig — Resolved Configuration Hook
 *
 * Loads and resolves the brand/market/model configuration for the
 * current session using resolveConfig() from @ims/shared.
 *
 * Resolution order (4-layer model):
 *   Brand config → Model config → Market config → Intersection override
 *
 * Session params are read from URL search params:
 *   ?brand=audi&model=q3sportback&market=de
 * Falls back to development defaults when params are absent.
 *
 * The Vite dev middleware serves /content/* from the monorepo root.
 * In production, /content/* is bundled into the dist output.
 *
 * @see docs/ims-brand-frontends/02-config-system.md
 * @see packages/shared/src/config/configResolver.ts
 */
import { useEffect, useState } from 'react';
import { resolveConfig, ConfigError } from '@ims/shared';
import type { ResolvedConfig } from '@ims/shared';
import { useAppStore } from '../store/app.store';

/** Read brand/market/preset from URL params; fall back to dev defaults. */
function getSessionParams() {
  const p = new URLSearchParams(window.location.search);
  return {
    brand: p.get('brand') ?? 'audi',
    // model is intentionally omitted here — resolved via selectedModelId store
    market: p.get('market') ?? 'de',
    preset: p.get('preset') ?? undefined,
    lang: p.get('lang') ?? undefined,
  };
}

/** Fallback model when nothing is selected yet (from URL param or hard default). */
function getUrlModel() {
  return new URLSearchParams(window.location.search).get('model') ?? 'rs5limo';
}

export interface UseConfigResult {
  config: ResolvedConfig | null;
  loading: boolean;
}

export function useConfig(): UseConfigResult {
  const [config, setConfig] = useState<ResolvedConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const setError = useAppStore((s) => s.setError);
  // Re-fetch config whenever the user selects a different model in the carousel.
  const selectedModelId = useAppStore((s) => s.selectedModelId);

  useEffect(() => {
    let cancelled = false;
    const { brand, market, preset, lang } = getSessionParams();
    // Prefer the store-selected model (set when "Start" is pressed),
    // fall back to the URL param for dev hot-reload convenience.
    const model = selectedModelId ?? getUrlModel();

    async function load() {
      setLoading(true);
      try {
        const resolved = await resolveConfig(brand, model, market, {
          baseUrl: '', // Vite middleware: /content/* → monorepo content/
          validate: import.meta.env.DEV, // schema validation in dev only
          presetId: preset,
          lang,
        });
        if (!cancelled) setConfig(resolved);
      } catch (err) {
        if (!cancelled) {
          const code = err instanceof ConfigError ? err.code : 'CONFIG_NOT_FOUND';
          setError({ code, recoverable: false });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [selectedModelId, setError]); // re-run when model selection changes

  return { config, loading };
}
